import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link, Navigate } from "react-router-dom";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { Layout } from "../../components/Layout/Layout";
import client from "../../api/client";
import { apiUpload } from "../../utils/apiProxy";
import {
  storeDayKey,
  effectiveDayKey,
  dayKeyToLabel,
  dayKeyToMonthLabel,
  weekdayAbbr,
  todayKeyUTC5,
  daysInStorage,
  isStale,
  STALE_DAYS,
} from "../../utils/storeDay";
import PostpaymentDue from "./PostpaymentDue";
import type { Workshop } from "../../utils/workshops";
import { EXPRESS_PRICE_KZT } from "../../utils/expressHelpers";
import { Document, Page, pdfjs } from "react-pdf";
import "./index.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface ArchiveRow {
  id: number;
  userId: number;
  username: string | null;
  staffId: number | null;
  staffName: string | null;
  coverPdfUrl: string | null;
  bookPdfUrl: string | null;
  pages: number;
  composition: Record<string, string>;
  express: "express_print" | "express_edit" | "urep" | null;
  storeDate: string | null;
  /** Книга лежит в «Отложке» — вне дате-папок, пока её не вернут в дату. */
  postponed: boolean;
  orderStatus: string | null;
  /** Цех заказа и цеха, под которые собраны файлы. Расходятся — файл надо
   *  пересобрать (идёт на фоне) или переделать руками (кастомная обложка). */
  workshop: Workshop | null;
  bookWorkshop: Workshop | null;
  coverWorkshop: Workshop | null;
  createdAt: string;
  bookName: string | null;
  author: string | null;
}

// «Отложка» — виртуальная папка рядом с дате-папками. Ключ занимает то же
// место в URL, что и день (/storage/postponed, /storage/postponed/:username),
// поэтому отдельный роут не нужен.
const POSTPONED_KEY = "postponed";

// Папка, в которой лежит книга: отложка либо складской день.
const folderKeyOf = (a: ArchiveRow): string =>
  a.postponed ? POSTPONED_KEY : effectiveDayKey(a);

const folderLabel = (key: string): string =>
  key === POSTPONED_KEY ? "Отложка" : dayKeyToLabel(key);

// «Экспресс книгами считаются только urep и express_print», см. требования.
// express_edit учитывается как обычная книга в подсчётах.
const isExpressForCount = (e: ArchiveRow["express"]) =>
  e === "urep" || e === "express_print";

// «Складской день» книги (YYYY-MM-DD) по правилу производства:
//  • до 10:00 (UTC+5) — текущий день, с 10:00 — следующий день;
//  • если день попал на выходной — переносим на понедельник
//    (суббота → +2 дня, воскресенье → +1). Поэтому папок на выходные нет.
// На вход — момент утверждения книги (book_archives.created_at).
// Excel-таблица со списком книг, попавших в zip: по строке на каждую книгу.
// Состав (формат/цветность/переплёт/…) у всех книг одинаковый — фиксированные
// значения производства. Возвращаем массив байт для JSZip.
// Считаем страницы прямо по PDF книги, скачанному в zip — это и есть
// источник правды. На stored book_archives.pages не опираемся (там бывают
// нули, если оператор не вписал число руками). pageCounts: archiveId → страниц,
// заполняется по ходу скачивания; fallback на r.pages, если PDF не открылся.
const countPdfPages = async (blob: Blob): Promise<number> => {
  const buf = await blob.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: new Uint8Array(buf) }).promise;
  return doc.numPages;
};

const buildBooksSheet = (
  users: UserStats[],
  pageCounts: Map<number, number>
): Uint8Array => {
  const rows = users.flatMap((u) =>
    u.rows.map((r) => ({
      "Имя клиента": u.username,
      "Название книги": r.bookName || "",
      Автор: r.author || "",
      Страницы: pageCounts.get(r.id) ?? r.pages,
      Формат: "120*180",
      Цветность: "4+4",
      Переплет: "тв.клей",
      Ламинация: "матовая",
      Бумага: "80гр",
      Тираж: 1,
    }))
  );
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [
    { wch: 30 }, // Имя клиента
    { wch: 30 }, // Название книги
    { wch: 30 }, // Автор
    { wch: 10 }, // Страницы
    { wch: 10 }, // Формат
    { wch: 10 }, // Цветность
    { wch: 10 }, // Переплет
    { wch: 10 }, // Ламинация
    { wch: 10 }, // Бумага
    { wch: 10 }, // Тираж
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Книги");
  return XLSX.write(wb, { type: "array", bookType: "xlsx" });
};

// Безопасное имя для папки/файла в zip — выкидываем спецсимволы файловой системы.
const sanitizeFsName = (s: string): string =>
  s.replace(/[\\/:*?"<>| -]/g, "_").trim() || "unnamed";

interface DayStats {
  key: string;
  label: string; // DD.MM.YY
  total: number;
  regular: number;
  express: number;
  /** Книг, которые лежат дольше STALE_DAYS и ещё не ушли в печать. */
  stale: number;
  rows: ArchiveRow[];
  earliestIso: string;
}

interface MonthGroup {
  key: string;
  label: string;
  earliestIso: string;
  days: DayStats[];
}

interface UserStats {
  username: string; // ключ + отображение
  userId: number;
  total: number;
  rows: ArchiveRow[];
}

// Максимум дней в хранилище среди залежавшихся книг юзера (0 — таких нет).
const staleDaysOf = (rows: ArchiveRow[]): number =>
  rows.reduce(
    (max, r) => (isStale(r) ? Math.max(max, daysInStorage(effectiveDayKey(r))) : max),
    0
  );

interface DownloadProgress {
  group: "regular" | "express";
  current: number;
  total: number;
  phase: "fetching" | "zipping";
}

const Storage: React.FC = () => {
  const navigate = useNavigate();
  const { day, username } = useParams<{ day?: string; username?: string }>();
  // Все ссылки внутри страницы — от этого корня.
  const base = "/storage";
  const [archives, setArchives] = useState<ArchiveRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [download, setDownload] = useState<DownloadProgress | null>(null);
  const [query, setQuery] = useState("");
  const [busyCardId, setBusyCardId] = useState<number | null>(null);

  // Массовый выбор книг внутри текущей папки (id архивов) + панель действий.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkDate, setBulkDate] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  // Попап «Отправить на печать».
  interface PrintUser {
    userId: number;
    label: string; // отображаемый ключ
    rawUsername: string | null; // реальный username для PATCH
    status: string | null;
  }
  const [printPopup, setPrintPopup] = useState<{ title: string; users: PrintUser[] } | null>(null);
  const [printChecked, setPrintChecked] = useState<Record<number, boolean>>({});
  const [printing, setPrinting] = useState(false);

  // ПКМ по пилу юзера в дне → контекстное меню «Сделать экспрессом».
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; user: UserStats } | null>(null);
  // Попап «Сделать экспрессом» — создаёт транзакцию УРЭП/ЭП, бэк сам
  // синхронизирует orders.express + book_archives.express.
  const [expressTarget, setExpressTarget] = useState<UserStats | null>(null);
  const [exType, setExType] = useState<"urep" | "express_print">("urep");
  const [exAmount, setExAmount] = useState<string>(String(EXPRESS_PRICE_KZT.urep));
  const [exCurrency, setExCurrency] = useState("KZT");
  const [exBank, setExBank] = useState("kaspi");
  const [exSaving, setExSaving] = useState(false);
  const [exError, setExError] = useState<string | null>(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const { data } = await client.get("/admin/archives", {
          params: { limit: 500 },
        });
        setArchives(data);
      } catch (err) {
        console.error("[storage] fetch failed", err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  // id клиентов с недоплатой: их книги нельзя отправить в печать (п. 3).
  // Ручка отдаёт только список id, без сумм, поэтому доступна и логисту.
  const [underpaid, setUnderpaid] = useState<Set<number>>(new Set());

  useEffect(() => {
    client
      .get<{ userIds: number[] }>("/transactions/underpaid-users")
      .then((r) => setUnderpaid(new Set(r.data?.userIds ?? [])))
      .catch((err) => console.error("[storage] underpaid-users fetch failed", err));
  }, []);

  // Папка каждого клиента для таблицы сбора постоплаты (п. 4 и 9). Если книг
  // несколько — берём самую свежую дате-папку; клиент, у которого все книги
  // в отложке, получает POSTPONED_KEY и из таблицы выпадает.
  const folderByUserId = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of archives) {
      const key = folderKeyOf(a);
      const id = String(a.userId);
      const prev = m.get(id);
      if (!prev) m.set(id, key);
      else if (prev === POSTPONED_KEY && key !== POSTPONED_KEY) m.set(id, key);
      else if (key !== POSTPONED_KEY && prev !== POSTPONED_KEY && key > prev) m.set(id, key);
    }
    return m;
  }, [archives]);

  // Книги в отложке — отдельная папка, из дате-папок они выпадают.
  const postponedRows = useMemo(
    () => archives.filter((a) => a.postponed),
    [archives]
  );

  // Группируем по складскому дню (storeDayKey), потом дни — в месяцы.
  const months = useMemo<MonthGroup[]>(() => {
    const dayMap = new Map<string, DayStats>();
    for (const a of archives) {
      if (a.postponed) continue;
      const k = effectiveDayKey(a);
      const existing = dayMap.get(k);
      if (existing) {
        existing.total += 1;
        if (isExpressForCount(a.express)) existing.express += 1;
        else existing.regular += 1;
        if (isStale(a)) existing.stale += 1;
        existing.rows.push(a);
      } else {
        dayMap.set(k, {
          key: k,
          label: dayKeyToLabel(k),
          total: 1,
          regular: isExpressForCount(a.express) ? 0 : 1,
          express: isExpressForCount(a.express) ? 1 : 0,
          stale: isStale(a) ? 1 : 0,
          rows: [a],
          earliestIso: a.createdAt,
        });
      }
    }

    const monthMap = new Map<string, MonthGroup>();
    for (const day of dayMap.values()) {
      const mLabel = dayKeyToMonthLabel(day.key);
      const existing = monthMap.get(mLabel);
      if (existing) {
        existing.days.push(day);
      } else {
        monthMap.set(mLabel, {
          key: mLabel,
          label: mLabel,
          earliestIso: day.key,
          days: [day],
        });
      }
    }

    // Ключи YYYY-MM-DD сортируются лексикографически = хронологически.
    // Дни внутри месяца — DESC, месяцы — DESC по самому свежему дню.
    const months = Array.from(monthMap.values());
    for (const m of months) {
      m.days.sort((a, b) => b.key.localeCompare(a.key));
    }
    months.sort((a, b) => b.days[0].key.localeCompare(a.days[0].key));
    return months;
  }, [archives]);

  // Список всех строк выбранной папки (складской день либо отложка).
  const dayRows: ArchiveRow[] = useMemo(() => {
    if (!day) return [];
    return archives.filter((a) => folderKeyOf(a) === day);
  }, [day, archives]);

  // Уникальные юзеры этого дня — для фильтра таблицы сбора постоплат.
  const dayUserIds = useMemo(
    () => Array.from(new Set(dayRows.map((r) => r.userId))),
    [dayRows]
  );

  // Юзеры за день, разделённые по типу: обычные / экспресс.
  // Юзер, у которого есть и те и те, попадёт в обе группы со своими книгами.
  const { regularUsers, expressUsers } = useMemo(() => {
    const buildGroup = (rows: ArchiveRow[]): UserStats[] => {
      const map = new Map<string, UserStats>();
      for (const r of rows) {
        const key = r.username || `userId ${r.userId}`;
        const existing = map.get(key);
        if (existing) {
          existing.total += 1;
          existing.rows.push(r);
        } else {
          map.set(key, { username: key, userId: r.userId, total: 1, rows: [r] });
        }
      }
      return Array.from(map.values()).sort((a, b) =>
        a.username.localeCompare(b.username, "ru")
      );
    };
    return {
      regularUsers: buildGroup(dayRows.filter((r) => !isExpressForCount(r.express))),
      expressUsers: buildGroup(dayRows.filter((r) => isExpressForCount(r.express))),
    };
  }, [dayRows]);

  // Книги выбранного юзера за выбранный день (для /storage/:day/:username).
  const userRows: ArchiveRow[] = useMemo(() => {
    if (!username) return [];
    const decoded = decodeURIComponent(username);
    return dayRows.filter(
      (r) => (r.username || `userId ${r.userId}`) === decoded
    );
  }, [username, dayRows]);

  // Поиск по username. Каждое совпадение — пара (день, username); клик
  // кидает на /storage/yyyy-mm-dd/username.
  const searchHits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const seen = new Set<string>();
    const hits: {
      username: string;
      dayKey: string;
      dayLabel: string;
      iso: string;
    }[] = [];
    for (const a of archives) {
      const uname = a.username || `userId ${a.userId}`;
      if (!uname.toLowerCase().includes(q)) continue;
      const dk = folderKeyOf(a);
      const key = `${dk}|${uname}`;
      if (seen.has(key)) continue;
      seen.add(key);
      hits.push({
        username: uname,
        dayKey: dk,
        dayLabel: folderLabel(dk),
        iso: a.createdAt,
      });
    }
    hits.sort((x, y) => new Date(y.iso).getTime() - new Date(x.iso).getTime());
    return hits.slice(0, 8);
  }, [query, archives]);

  const goToHit = (h: { username: string; dayKey: string }) => {
    navigate(`${base}/${h.dayKey}/${encodeURIComponent(h.username)}`);
    setQuery("");
  };

  // Локально обновляем строку архива (после PATCH), чтобы UI/группировка
  // пересчитались без перезапроса всего списка.
  const patchLocalArchive = (id: number, patch: Partial<ArchiveRow>) =>
    setArchives((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));

  // Замена PDF обложки/книги: грузим файл в /upload, затем PATCH архива.
  const handleReplaceFile = async (
    row: ArchiveRow,
    kind: "cover" | "book",
    file: File
  ) => {
    setBusyCardId(row.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiUpload("/upload", fd);
      if (!res.ok) throw new Error(`upload failed: ${res.status}`);
      const { url } = await res.json();
      const body =
        kind === "cover" ? { coverPdfUrl: url } : { bookPdfUrl: url };
      await client.patch(`/admin/archives/${row.id}`, body);
      patchLocalArchive(row.id, body);
    } catch (err) {
      console.error("[storage] replace file failed", err);
      alert("Не удалось заменить файл. См. консоль.");
    } finally {
      setBusyCardId(null);
    }
  };

  // Перенос книги в другую дату-папку (или сброс override на авто-расчёт).
  // Книгу из отложки такой перенос заодно возвращает в дате-папки.
  // После успеха переходим на новый день, чтобы книга осталась видимой.
  const handleSetDate = async (row: ArchiveRow, newStoreDate: string | null) => {
    const targetKey = newStoreDate || storeDayKey(row.createdAt);
    if (!row.postponed && targetKey === effectiveDayKey(row)) return;
    // На выходные переносить нельзя — только будни.
    if (newStoreDate) {
      const dow = new Date(`${newStoreDate}T00:00:00Z`).getUTCDay();
      if (dow === 0 || dow === 6) {
        alert("Нельзя перенести на выходной.");
        return;
      }
    }
    setBusyCardId(row.id);
    try {
      const body: Partial<ArchiveRow> = { storeDate: newStoreDate };
      if (row.postponed) body.postponed = false;
      await client.patch(`/admin/archives/${row.id}`, body);
      patchLocalArchive(row.id, body);
      const uname = row.username || `userId ${row.userId}`;
      navigate(`${base}/${targetKey}/${encodeURIComponent(uname)}`);
    } catch (err) {
      console.error("[storage] set date failed", err);
      alert("Не удалось перенести дату. См. консоль.");
    } finally {
      setBusyCardId(null);
    }
  };

  // Отложить одну книгу / вернуть её из отложки. После успеха переходим в ту
  // папку, куда книга уехала, чтобы она осталась на глазах.
  const handleTogglePostponed = async (row: ArchiveRow) => {
    const next = !row.postponed;
    setBusyCardId(row.id);
    try {
      await client.patch(`/admin/archives/${row.id}`, { postponed: next });
      patchLocalArchive(row.id, { postponed: next });
      const uname = row.username || `userId ${row.userId}`;
      const targetKey = next ? POSTPONED_KEY : effectiveDayKey(row);
      navigate(`${base}/${targetKey}/${encodeURIComponent(uname)}`);
    } catch (err) {
      console.error("[storage] toggle postponed failed", err);
      alert("Не удалось перенести книгу. См. консоль.");
    } finally {
      setBusyCardId(null);
    }
  };

  // ── Массовый выбор книг ────────────────────────────────────────────────
  // Выделение живёт в рамках одной папки: при смене дня/юзера сбрасываем,
  // иначе можно случайно перенести книги, которых уже не видно на экране.
  useEffect(() => {
    setSelected(new Set());
    setBulkError(null);
  }, [day, username]);

  const toggleSelected = (ids: number[], on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  const clearSelection = () => setSelected(new Set());

  // Общий вызов bulk-PATCH: правим выбранные архивы, синхроним локальный стейт.
  const applyBulk = async (patch: { storeDate?: string | null; postponed?: boolean }) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    setBulkError(null);
    try {
      await client.patch("/admin/archives/bulk", { ids, ...patch });
      const idSet = new Set(ids);
      setArchives((prev) =>
        prev.map((a) => (idSet.has(a.id) ? { ...a, ...patch } : a))
      );
      clearSelection();
    } catch (err: any) {
      console.error("[storage] bulk patch failed", err);
      setBulkError(err?.response?.data?.error || err?.message || "Не удалось применить");
    } finally {
      setBulkBusy(false);
    }
  };

  // Перенос выбранных в дату. Из отложки — заодно возвращает в дате-папки.
  const bulkMoveToDate = async () => {
    if (!bulkDate) return;
    const dow = new Date(`${bulkDate}T00:00:00Z`).getUTCDay();
    if (dow === 0 || dow === 6) {
      setBulkError("Нельзя перенести на выходной.");
      return;
    }
    await applyBulk({ storeDate: bulkDate, postponed: false });
  };

  // Открыть попап отправки на печать для группы (обычные/экспресс).
  // По дефолту все юзеры отмечены.
  const openPrintPopup = (title: string, users: UserStats[]) => {
    const list: PrintUser[] = users.map((u) => ({
      userId: u.userId,
      label: u.username,
      rawUsername: u.rows[0]?.username ?? null,
      status: u.rows[0]?.orderStatus ?? null,
    }));
    const checked: Record<number, boolean> = {};
    list.forEach((u) => {
      // Недоплативших не отмечаем и не даём отметить — см. рендер попапа.
      checked[u.userId] = !underpaid.has(u.userId);
    });
    setPrintChecked(checked);
    setPrintPopup({ title, users: list });
  };

  // Отправить на печать всех отмеченных: статус заказа → «Отправлено на печать».
  const handleSendToPrint = async () => {
    if (!printPopup) return;
    const targets = printPopup.users.filter(
      (u) => printChecked[u.userId] && u.rawUsername && !underpaid.has(u.userId)
    );
    if (targets.length === 0) {
      setPrintPopup(null);
      return;
    }
    setPrinting(true);
    try {
      await Promise.all(
        targets.map((u) =>
          client.patch(`/orders/${encodeURIComponent(u.rawUsername!)}`, {
            status: "Отправлено на печать",
          })
        )
      );
      const ids = new Set(targets.map((u) => u.userId));
      setArchives((prev) =>
        prev.map((a) =>
          ids.has(a.userId) ? { ...a, orderStatus: "Отправлено на печать" } : a
        )
      );
      setPrintPopup(null);
    } catch (err) {
      console.error("[storage] send to print failed", err);
      alert("Не удалось отправить на печать. См. консоль.");
    } finally {
      setPrinting(false);
    }
  };

  // Открыть контекстное меню по ПКМ на пиле юзера: «Сделать экспрессом»
  // (если ещё не экспресс).
  const isUserExpress = (user: UserStats) =>
    user.rows.some((r) => isExpressForCount(r.express));
  const openExpressCtx = (e: React.MouseEvent, user: UserStats) => {
    if (isUserExpress(user)) return;
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, user });
  };

  // Открыть попап «Сделать экспрессом» для юзера из контекстного меню.
  const openExpressModal = (user: UserStats) => {
    setExpressTarget(user);
    setExType("urep");
    setExAmount(String(EXPRESS_PRICE_KZT.urep));
    setExCurrency("KZT");
    setExBank("kaspi");
    setExError(null);
    setCtxMenu(null);
  };

  // Создать транзакцию экспресс-услуги → юзер становится экспрессом.
  const handleMakeExpress = async () => {
    if (!expressTarget) return;
    setExSaving(true);
    setExError(null);
    try {
      await client.post("/transactions", {
        userId: expressTarget.userId,
        paymentType: exType,
        amount: Number(exAmount) || 0,
        currency: exCurrency,
        bank: exBank,
      });
      // Локально проставим express всем строкам юзера, чтобы он переехал
      // из «Обычные» в «Экспресс» без перезапроса.
      setArchives((prev) =>
        prev.map((a) =>
          a.userId === expressTarget.userId ? { ...a, express: exType } : a
        )
      );
      setExpressTarget(null);
    } catch (err: any) {
      setExError(err?.response?.data?.error || err?.message || "Ошибка");
    } finally {
      setExSaving(false);
    }
  };

  const todayKey = todayKeyUTC5();

  const isUserView = Boolean(day && username);
  const isDayView = Boolean(day && !username);
  const isRoot = !day && !username;

  // Хлебные крошки.
  const breadcrumbs = useMemo(() => {
    if (isRoot) return null;
    const isPostponed = day === POSTPONED_KEY;
    const monthLabel = day && !isPostponed ? dayKeyToMonthLabel(day) : "";
    const dayLabel = day ? folderLabel(day) : "";
    return (
      <nav className="st-crumbs" aria-label="Навигация">
        <Link to={base} className="st-crumb">
          Хранилище
        </Link>
        {day && (
          <>
            {/* У отложки нет месяца — крошка сразу «Хранилище › Отложка». */}
            {!isPostponed && (
              <>
                <span className="st-crumb-sep">›</span>
                <span className="st-crumb">{monthLabel}</span>
              </>
            )}
            <span className="st-crumb-sep">›</span>
            {isUserView ? (
              <Link to={`${base}/${day}`} className="st-crumb">
                {dayLabel}
              </Link>
            ) : (
              <span className="st-crumb st-crumb-current">{dayLabel}</span>
            )}
          </>
        )}
        {isUserView && (
          <>
            <span className="st-crumb-sep">›</span>
            <span className="st-crumb st-crumb-current">
              {decodeURIComponent(username!)}
            </span>
          </>
        )}
      </nav>
    );
  }, [isRoot, isUserView, day, username, base]);

  // Скачивание группы (обычные / экспресс) одним zip.
  // Для каждого юзера — папка, внутри обложка.pdf и книга.pdf (с индексом,
  // если книг несколько). Москва просит имена по названию книги:
  // <название>_обложка.pdf и <название>_блок.pdf.
  const downloadGroup = async (
    group: "regular" | "express",
    users: UserStats[]
  ) => {
    if (!day || users.length === 0 || download) return;

    // Считаем общее число файлов, чтобы прогресс был информативным.
    const totalFiles = users.reduce(
      (sum, u) =>
        sum +
        u.rows.reduce(
          (n, r) => n + (r.coverPdfUrl ? 1 : 0) + (r.bookPdfUrl ? 1 : 0),
          0
        ),
      0
    );
    if (totalFiles === 0) return;

    setDownload({ group, current: 0, total: totalFiles, phase: "fetching" });

    const zip = new JSZip();
    let done = 0;
    // archiveId → реальное число страниц книги, посчитанное по скачанному PDF.
    const pageCounts = new Map<number, number>();

    try {
      for (const user of users) {
        const folder = zip.folder(sanitizeFsName(user.username));
        if (!folder) continue;

        for (let i = 0; i < user.rows.length; i++) {
          const row = user.rows[i];
          const suffix = user.rows.length > 1 ? `-${i + 1}` : "";

          for (const [url, baseName, isBook] of [
            [row.coverPdfUrl, `обложка${suffix}.pdf`, false],
            [row.bookPdfUrl, `книга${suffix}.pdf`, true],
          ] as const) {
            if (!url) continue;
            try {
              // CDN-урлы Spaces без CORS — тянем через бэкенд-прокси.
              // Локальные /uploads/ ходят напрямую (тот же домен с CORS).
              const isSpaces = /\.digitaloceanspaces\.com\/|\/\/(comabooks|gtaxi)\.s3(\.[a-z0-9-]+)?\.amazonaws\.com\//i.test(url);
              const res = isSpaces
                ? await client.get("/admin/archives/proxy", {
                    params: { url },
                    responseType: "blob",
                  })
                : await client.get(url, { responseType: "blob" });
              const blob: Blob = res.data;
              folder.file(baseName, blob);
              // Книгу, попавшую в zip, сразу считаем для excel-таблицы.
              if (isBook) {
                try {
                  pageCounts.set(row.id, await countPdfPages(blob));
                } catch (err) {
                  console.error("[storage] page count failed", url, err);
                }
              }
            } catch (err) {
              console.error("[storage] file fetch failed", url, err);
              // Кладём текстовый stub, чтобы было понятно, что именно недокачалось.
              folder.file(
                `${baseName}.ERROR.txt`,
                `Не удалось скачать: ${url}\n${String(err)}`
              );
            }
            done += 1;
            setDownload({ group, current: done, total: totalFiles, phase: "fetching" });
          }
        }
      }

      setDownload({ group, current: totalFiles, total: totalFiles, phase: "zipping" });
      const groupLabel = group === "regular" ? "обычные" : "экспресс";
      // Таблица книг этого zip — в корень архива.
      zip.file(
        `Книги ${folderLabel(day)} ${groupLabel}.xlsx`,
        buildBooksSheet(users, pageCounts)
      );
      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(blob, `${folderLabel(day)}-${groupLabel}.zip`);
    } catch (err) {
      console.error("[storage] download failed", err);
      alert("Не удалось собрать архив. См. консоль.");
    } finally {
      setDownload(null);
    }
  };

  const renderUserGroup = (
    title: string,
    group: "regular" | "express",
    users: UserStats[]
  ) => {
    if (users.length === 0) return null;
    const isDownloading = download?.group === group;
    const totalBooks = users.reduce((n, u) => n + u.total, 0);

    return (
      <section className="st-group">
        <div className="st-group-head">
          <h3 className="st-group-title">
            {title}{" "}
            <span className="st-group-count">
              · {users.length} юзеров · {totalBooks} кн.
            </span>
          </h3>
          <div className="st-group-actions">
            <button
              className="st-dl-btn"
              onClick={() => openPrintPopup(title, users)}
            >
              Отправить на печать
            </button>
            <button
              className="st-dl-btn"
              disabled={Boolean(download)}
              onClick={() => downloadGroup(group, users)}
            >
              {isDownloading
                ? download!.phase === "zipping"
                  ? "Архивация…"
                  : `Скачивание ${download!.current}/${download!.total}`
                : "Скачать архив"}
            </button>
          </div>
        </div>

        {isDownloading && (
          <div className="st-progress">
            <div
              className="st-progress-bar"
              style={{
                width:
                  download!.phase === "zipping"
                    ? "100%"
                    : `${Math.round((download!.current / download!.total) * 100)}%`,
              }}
            />
          </div>
        )}

        <div className="st-pills">
          {users.map((u) => {
            // Чекбокс на пиле выделяет все книги юзера в этой группе разом.
            const ids = u.rows.map((r) => r.id);
            const allChecked = ids.every((id) => selected.has(id));
            const staleDays = staleDaysOf(u.rows);
            return (
              <div
                key={u.username}
                className={
                  "st-pill-wrap" +
                  (allChecked ? " st-pill-wrap--checked" : "") +
                  (staleDays > 0 ? " st-pill-wrap--stale" : "")
                }
              >
                <input
                  type="checkbox"
                  className="st-pill-check"
                  checked={allChecked}
                  aria-label={`Выбрать книги ${u.username}`}
                  onChange={(e) => toggleSelected(ids, e.target.checked)}
                />
                <button
                  className="st-pill"
                  onClick={() =>
                    navigate(`${base}/${day}/${encodeURIComponent(u.username)}`)
                  }
                  onContextMenu={(e) => openExpressCtx(e, u)}
                >
                  <span className="st-pill-date">{u.username}</span>
                  <span className="st-pill-total">{u.total} кн.</span>
                  <span className="st-pill-status">
                    {u.rows[0]?.orderStatus || "—"}
                  </span>
                  {staleDays > 0 && (
                    <span className="st-pill-stale">лежит {staleDays} дн.</span>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  // Плавающая панель массовых действий — видна, пока что-то выбрано.
  const renderBulkBar = () => {
    if (selected.size === 0) return null;
    const inPostponed = day === POSTPONED_KEY;
    return (
      <div className="st-bulk">
        <span className="st-bulk-count">Выбрано: {selected.size} кн.</span>
        {bulkError && <span className="st-bulk-error">{bulkError}</span>}
        <input
          type="date"
          className="st-bulk-date"
          value={bulkDate}
          disabled={bulkBusy}
          onChange={(e) => setBulkDate(e.target.value)}
        />
        <button
          className="st-bulk-btn st-bulk-btn--primary"
          disabled={bulkBusy || !bulkDate}
          onClick={bulkMoveToDate}
        >
          {bulkBusy ? "…" : inPostponed ? "Вернуть в дату" : "Перенести"}
        </button>
        {inPostponed ? (
          <button
            className="st-bulk-btn"
            disabled={bulkBusy}
            onClick={() => applyBulk({ postponed: false })}
            title="Вернуть в дате-папку по её текущей дате"
          >
            Убрать из отложки
          </button>
        ) : (
          <button
            className="st-bulk-btn"
            disabled={bulkBusy}
            onClick={() => applyBulk({ postponed: true })}
          >
            В отложку
          </button>
        )}
        <button className="st-bulk-btn" disabled={bulkBusy} onClick={clearSelection}>
          Снять
        </button>
      </div>
    );
  };

  return (
    <Layout>
      <div className="st-head">
        <h2 className="st-title">Хранилище</h2>
        <div className="st-search">
          <input
            className="st-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Поиск по username…"
          />
          {searchHits.length > 0 && (
            <div className="st-search-dropdown">
              {searchHits.map((h) => (
                <button
                  key={`${h.dayKey}|${h.username}`}
                  className="st-search-item"
                  onClick={() => goToHit(h)}
                >
                  <span className="st-search-name">{h.username}</span>
                  <span className="st-search-day">{h.dayLabel}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {breadcrumbs}

      {loading ? (
        <div className="st-empty">Загрузка…</div>
      ) : isRoot ? (
        archives.length === 0 ? (
          <div className="st-empty">Пока ни одной утверждённой книги</div>
        ) : (
          <>
            {/* Отложка — отдельная папка над месяцами. Показываем всегда,
                чтобы было видно, куда уезжают отложенные книги. */}
            <section className="st-month">
              <h3 className="st-month-title">Отложка</h3>
              <div className="st-pills">
                <button
                  className="st-pill"
                  onClick={() => navigate(`${base}/${POSTPONED_KEY}`)}
                >
                  <span className="st-pill-date">Отложка</span>
                  <span className="st-pill-total">{postponedRows.length} кн.</span>
                </button>
              </div>
            </section>

            {months.map((m) => (
            <section key={m.key} className="st-month">
              <h3 className="st-month-title">{m.label}</h3>
              <div className="st-pills">
                {m.days.map((d) => (
                  <button
                    key={d.key}
                    className="st-pill"
                    onClick={() => navigate(`${base}/${d.key}`)}
                  >
                    <span className="st-pill-date">
                      {d.key === todayKey ? "Сегодня" : d.label}, {weekdayAbbr(d.key)}
                    </span>
                    <span className="st-pill-total">{d.total} кн.</span>
                    <span className="st-pill-counts">
                      <span className="st-pill-reg">{d.regular} обыч.</span>
                      <span className="st-pill-exp">{d.express} экспр.</span>
                    </span>
                    {d.stale > 0 && (
                      <span className="st-pill-stale">{d.stale} залежались</span>
                    )}
                  </button>
                ))}
              </div>
            </section>
            ))}
          </>
        )
      ) : isDayView ? (
        regularUsers.length === 0 && expressUsers.length === 0 ? (
          <div className="st-empty">
            {day === POSTPONED_KEY ? "В отложке пусто" : "За этот день нет книг"}
          </div>
        ) : (
          <>
            {renderUserGroup("Обычные", "regular", regularUsers)}
            {renderUserGroup("Экспресс", "express", expressUsers)}
          </>
        )
      ) : (
        // isUserView
        userRows.length === 0 ? (
          <div className="st-empty">
            {day === POSTPONED_KEY
              ? "У этого юзера нет книг в отложке"
              : "У этого юзера нет книг за этот день"}
          </div>
        ) : (
          <div className="st-grid">
            {userRows.map((row) => (
              <div
                key={row.id}
                className={`st-card${selected.has(row.id) ? " st-card--checked" : ""}`}
              >
                <div className="st-card-head">
                  {isStale(row) && (
                    <span
                      className="st-badge st-badge-stale"
                      title={`В хранилище больше ${STALE_DAYS} дней и ещё не отправлена в печать`}
                    >
                      залежалась {daysInStorage(effectiveDayKey(row))} дн.
                    </span>
                  )}
                  <input
                    type="checkbox"
                    className="st-card-check"
                    checked={selected.has(row.id)}
                    aria-label="Выбрать книгу"
                    onChange={(e) => toggleSelected([row.id], e.target.checked)}
                  />
                  <span className="st-card-username">
                    {row.username || `userId ${row.userId}`}
                  </span>
                  {row.express && (
                    <span className={`st-badge st-badge-${row.express}`}>
                      {row.express === "urep"
                        ? "УРЭП"
                        : row.express === "express_print"
                          ? "Экспресс печать"
                          : "Уск. редактура"}
                    </span>
                  )}
                </div>
                <div className="st-card-thumbs">
                  {row.coverPdfUrl && (
                    <a
                      href={row.coverPdfUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="st-thumb"
                      title="Обложка"
                    >
                      <Document
                        file={row.coverPdfUrl}
                        loading={<div className="st-thumb-empty">…</div>}
                        error={<div className="st-thumb-empty">err</div>}
                      >
                        <Page
                          pageNumber={1}
                          width={120}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                        />
                      </Document>
                      <span className="st-thumb-label">Обложка</span>
                    </a>
                  )}
                  {row.bookPdfUrl && (
                    <a
                      href={row.bookPdfUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="st-thumb"
                      title="Книга"
                    >
                      <Document
                        file={row.bookPdfUrl}
                        loading={<div className="st-thumb-empty">…</div>}
                        error={<div className="st-thumb-empty">err</div>}
                      >
                        <Page
                          pageNumber={1}
                          width={120}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                        />
                      </Document>
                      <span className="st-thumb-label">Книга</span>
                    </a>
                  )}
                </div>
                <div className="st-card-meta">
                  <span>{row.pages} стр.</span>
                  <span>•</span>
                  <span>{row.staffName || "—"}</span>
                </div>

                <div className="st-card-edit">
                  <div className="st-edit-row">
                    <span className="st-edit-label">Дата</span>
                    <input
                      type="date"
                      className="st-edit-date"
                      value={effectiveDayKey(row)}
                      disabled={busyCardId === row.id}
                      onChange={(e) => handleSetDate(row, e.target.value || null)}
                    />
                    {row.storeDate && (
                      <button
                        type="button"
                        className="st-edit-reset"
                        disabled={busyCardId === row.id}
                        title="Вернуть авто-расчёт дня"
                        onClick={() => handleSetDate(row, null)}
                      >
                        авто
                      </button>
                    )}
                    <button
                      type="button"
                      className="st-edit-reset"
                      disabled={busyCardId === row.id}
                      title={
                        row.postponed
                          ? "Вернуть книгу в дате-папку"
                          : "Убрать книгу из дате-папок в отложку"
                      }
                      onClick={() => handleTogglePostponed(row)}
                    >
                      {row.postponed ? "из отложки" : "в отложку"}
                    </button>
                  </div>

                  <div className="st-edit-files">
                    <label className="st-edit-file">
                      {row.coverPdfUrl ? "Заменить обложку" : "Загрузить обложку"}
                      <input
                        type="file"
                        accept="application/pdf"
                        hidden
                        disabled={busyCardId === row.id}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleReplaceFile(row, "cover", f);
                          e.currentTarget.value = "";
                        }}
                      />
                    </label>
                    <label className="st-edit-file">
                      {row.bookPdfUrl ? "Заменить книгу" : "Загрузить книгу"}
                      <input
                        type="file"
                        accept="application/pdf"
                        hidden
                        disabled={busyCardId === row.id}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleReplaceFile(row, "book", f);
                          e.currentTarget.value = "";
                        }}
                      />
                    </label>
                  </div>

                  {busyCardId === row.id && (
                    <div className="st-edit-busy">Сохранение…</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Постоплату собирают с книг этого цеха — у остальных папки здесь нет. */}
      {isRoot && (
        <PostpaymentDue
          basePath={base}
          filterUserIds={archives.map((a) => a.userId)}
          folderByUserId={folderByUserId}
        />
      )}
      {isDayView && (
        <PostpaymentDue basePath={base} filterUserIds={dayUserIds} folderByUserId={folderByUserId} />
      )}

      {renderBulkBar()}

      {ctxMenu && (
        <>
          <div
            className="st-ctx-backdrop"
            onClick={() => setCtxMenu(null)}
            onContextMenu={(e) => {
              e.preventDefault();
              setCtxMenu(null);
            }}
          />
          <div
            className="st-ctx"
            style={{ top: ctxMenu.y, left: ctxMenu.x }}
            onClick={(e) => e.stopPropagation()}
          >
            {!isUserExpress(ctxMenu.user) && (
              <button
                className="st-ctx-item"
                onClick={() => openExpressModal(ctxMenu.user)}
              >
                Сделать экспрессом
              </button>
            )}
          </div>
        </>
      )}

      {expressTarget && (
        <div
          className="st-print-overlay"
          onClick={() => !exSaving && setExpressTarget(null)}
        >
          <div className="st-print-modal" onClick={(e) => e.stopPropagation()}>
            <div className="st-print-head">
              <h3 className="st-print-title">
                Сделать экспрессом · {expressTarget.username}
              </h3>
              <button
                className="st-print-close"
                onClick={() => setExpressTarget(null)}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>

            <div className="st-ex-body">
              {exError && <div className="st-ex-error">{exError}</div>}

              <label className="st-ex-field">
                <span>Тип</span>
                <select
                  className="st-ex-select"
                  value={exType}
                  onChange={(e) => {
                    const v = e.target.value as "urep" | "express_print";
                    setExType(v);
                    setExAmount(String(EXPRESS_PRICE_KZT[v]));
                  }}
                >
                  <option value="urep">УРЭП · 15000 ₸</option>
                  <option value="express_print">Экспресс печать · 9000 ₸</option>
                </select>
              </label>

              <label className="st-ex-field">
                <span>Сумма</span>
                <input
                  className="st-ex-input"
                  type="number"
                  value={exAmount}
                  onChange={(e) => setExAmount(e.target.value)}
                />
              </label>

              <div className="st-ex-row">
                <label className="st-ex-field" style={{ flex: 1 }}>
                  <span>Валюта</span>
                  <select
                    className="st-ex-select"
                    value={exCurrency}
                    onChange={(e) => setExCurrency(e.target.value)}
                  >
                    {["KZT", "USD", "AZN", "BYN", "GEL", "TRY", "RUB", "EUR", "KGS"].map(
                      (c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      )
                    )}
                  </select>
                </label>
                <label className="st-ex-field" style={{ flex: 1 }}>
                  <span>Счёт</span>
                  <select
                    className="st-ex-select"
                    value={exBank}
                    onChange={(e) => setExBank(e.target.value)}
                  >
                    {["kaspi", "kaspi_gold", "kaspi_pay", "vtb", "tinkoff", "optima", "halyk", "stripe", "cash", "other"].map(
                      (b) => (
                        <option key={b} value={b}>
                          {b}
                        </option>
                      )
                    )}
                  </select>
                </label>
              </div>
            </div>

            <button
              className="st-print-submit"
              disabled={exSaving}
              onClick={handleMakeExpress}
            >
              {exSaving ? "Сохранение…" : "Сделать экспрессом"}
            </button>
          </div>
        </div>
      )}

      {printPopup && (
        <div className="st-print-overlay" onClick={() => setPrintPopup(null)}>
          <div className="st-print-modal" onClick={(e) => e.stopPropagation()}>
            <div className="st-print-head">
              <h3 className="st-print-title">
                Отправить на печать · {printPopup.title}
              </h3>
              <button
                className="st-print-close"
                onClick={() => setPrintPopup(null)}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>

            <div className="st-print-list">
              {printPopup.users.map((u) => {
                const blocked = underpaid.has(u.userId);
                return (
                  <label
                    key={u.userId}
                    className="st-print-row"
                    title={
                      blocked
                        ? "Книга оплачена не полностью — сначала соберите постоплату"
                        : undefined
                    }
                    style={blocked ? { opacity: 0.55 } : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={!!printChecked[u.userId] && !blocked}
                      disabled={blocked}
                      onChange={(e) =>
                        setPrintChecked((p) => ({ ...p, [u.userId]: e.target.checked }))
                      }
                    />
                    <span className="st-print-name">{u.label}</span>
                    <span className="st-print-status">
                      {blocked ? "не оплачено" : u.status || "—"}
                    </span>
                  </label>
                );
              })}
            </div>

            <button
              className="st-print-submit"
              disabled={printing}
              onClick={handleSendToPrint}
            >
              {printing ? "Отправка…" : "Отправить на печать"}
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
};

// /storage[/:day[/:username]]. Цех один (Алматы); старые ссылки с цехом
// (/storage/almaty/…, /storage/moscow/…) уводим на общий вид без него.
const LEGACY_WORKSHOPS = ["almaty", "moscow"];
const StoragePage: React.FC = () => {
  const { workshop, day, username } = useParams<{
    workshop?: string;
    day?: string;
    username?: string;
  }>();
  if (workshop || (day && LEGACY_WORKSHOPS.includes(day))) {
    const rest = (workshop ? [day, username] : [username]).filter(Boolean).join("/");
    return <Navigate to={`/storage${rest ? `/${rest}` : ""}`} replace />;
  }
  return <Storage />;
};

export default StoragePage;
